/**
 * READ-ORDER SSOT for the offer fields that carry a legacy Zoho-cased twin beside their canonical D1 column. The
 * offer twin of `const/transaction-status-field-keys.ts`, minted for the same reason and with the same shape: the read
 * ORDER is the decision, and homing it as DATA is the only thing that stops eight independent readers from each
 * re-deciding it.
 *
 * WHY CANONICAL LEADS, established from the WRITER side rather than inherited from the transaction leaf's convention:
 *
 *  - `flows/offer-create.ts` is the SOLE writer of `offers.offer_amount` (its own docstring says so, and it is the leaf
 *    carrying the whole-cent producer gate). It writes the canonical snake_case column ONLY. `flows/offer-status-update.ts`
 *    is the status counterpart and likewise reads and writes `offer_status` alone.
 *  - NO producer writes `Offer_Amount` or `Offer_Status` as a primary value. The single writer of either legacy spelling
 *    is `normalizeOfferToLegacy` (`utils/property/nested-list-offer-normalize.ts`), which COPIES the canonical value onto
 *    the Pascal key. The legacy half is therefore a DERIVED MIRROR of the canonical half, never an independent source,
 *    and there is no legacy migration window in which it can carry a value the canonical column does not.
 *  - That mirror goes STALE by construction: `normalizeOfferToLegacy` skips the copy whenever the Pascal key is already
 *    present, so an update that moves `offer_amount` leaves an older `Offer_Amount` sitting beside it. A legacy-first
 *    reader therefore serves the STALE value while the canonical column holds the current one. Canonical-first is not a
 *    style preference here; it is the only order that reads the authoritative half.
 *
 * Homed in `const/` rather than beside any one reader for the transaction leaf's reason, unchanged: the consumers sit in
 * `apps/`, `flows/`, `tools/` and `utils/`, and a `tools/`->`tools/` import is a permanent ban
 * (`QUAL-NO-CROSS-TOOL-IMPORTS-001`).
 *
 * Paired forbid-rule: `QUAL-OFFER-LEGACY-FIELD-PAIR-SSOT-001`, which maps over `OFFER_LEGACY_FIELD_KEY_TUPLES` below,
 * so the rule cannot describe a vocabulary this leaf does not.
 *
 * THE VOCABULARY GREW FROM TWO PAIRS TO FIVE ON 2026-09-04, and the ground for each addition is written at its tuple
 * rather than assumed from the first two: `bulk_offer`/`Bulk_Offer`, `auction_bid`/`Auction_Bid` and
 * `buyer_id`/`Buyer_ID` were each being read or listed as a hand-spelled adjacent pair at two or more sites while this
 * leaf governed neither half, so the read order was re-decided per site exactly as the amount pair's was before this
 * leaf existed. Two of those sites read the LEGACY half first.
 *
 * IT GREW AGAIN TO SEVEN LATER THE SAME DAY with `days_to_close`/`Days_to_Close` and `days_to_cancel`/`Days_to_Cancel`,
 * which the paired rule had recorded as owed-but-undecidable because the pair's only writer spelled the legacy half
 * `Days_To_Close` (capital `To`) -- a spelling NO reader looked for. That writer was corrected in the same change, which
 * is what supplied the writer-side grounding this leaf requires of every tuple; the per-tuple docstrings carry the
 * census that settled it.
 */

import { FORM_FIELDS } from '@federa/const/reducer-actions';

/** The legacy Zoho-cased offer field names, as the only place each string is spelled.
 *
 *  MODULE-LOCAL, not exported, for the reason the transaction twin's `LEGACY_TRANSACTION_STATUS_FIELDS` records: this
 *  record is the INGREDIENT and the two `*_FIELD_KEYS` tuples are what the leaf hands out. A reader spreads an ordered
 *  tuple, never a raw legacy name -- which is the whole point of homing the read ORDER here -- so exporting it would
 *  mint a `QUAL-DEAD-EXPORT-003` orphan at zero external occurrences on day one. */
const LEGACY_OFFER_FIELDS = {
	OFFER_AMOUNT: 'Offer_Amount',
	OFFER_STATUS: 'Offer_Status',
	BULK_OFFER: 'Bulk_Offer',
	AUCTION_BID: 'Auction_Bid',
	BUYER_ID: 'Buyer_ID',
	DAYS_TO_CLOSE: 'Days_to_Close',
	DAYS_TO_CANCEL: 'Days_to_Cancel',
} as const;

/** The CANONICAL columns of the three pairs added 2026-09-04, spelled here because no existing const names them.
 *
 *  `FORM_FIELDS` is the source for the amount and status halves and is used for them below; it declares no member for
 *  `bulk_offer`, `auction_bid` or `buyer_id`, and neither does `DB_COLUMNS` (checked at both leaves before spelling
 *  them here rather than assumed). All three are real columns -- `bulk_offer` and `buyer_id` are declared on
 *  `types/d1-offers.ts`, `auction_bid` on the offer API shape -- so this record is the FIRST spelling of each, not a
 *  second one. MODULE-LOCAL for the same reason `LEGACY_OFFER_FIELDS` is: a reader spreads a tuple, never a raw half.
 *  When `FORM_FIELDS` or `DB_COLUMNS` grows a member for one of these columns, the row here is deleted and the tuple
 *  reads from that constant instead -- that is the direction, and it is the only edit this leaf then needs. */
const CANONICAL_OFFER_COLUMNS = {
	BULK_OFFER: 'bulk_offer',
	AUCTION_BID: 'auction_bid',
	BUYER_ID: 'buyer_id',
	DAYS_TO_CLOSE: 'days_to_close',
	DAYS_TO_CANCEL: 'days_to_cancel',
} as const;

/** Offer-amount read order: canonical column, then the legacy twin. Spread into any variadic key reader. */
export const OFFER_AMOUNT_FIELD_KEYS = [FORM_FIELDS.OFFER_AMOUNT, LEGACY_OFFER_FIELDS.OFFER_AMOUNT] as const;

/** Offer-status read order: canonical column, then the legacy twin. Spread into any variadic key reader. */
export const OFFER_STATUS_FIELD_KEYS = [FORM_FIELDS.OFFER_STATUS, LEGACY_OFFER_FIELDS.OFFER_STATUS] as const;

/** Bulk-offer (bid discriminator) read order: canonical column, then the legacy twin.
 *
 *  CANONICAL-FIRST ON THE SAME WRITER-SIDE EVIDENCE the amount and status tuples rest on, established here rather than
 *  inherited: `bulk_offer` is the D1 column (`types/d1-offers.ts`), and the ONLY writers of `Bulk_Offer` are
 *  `normalizeOfferToLegacy` (`utils/property/nested-list-offer-normalize.ts`), which copies canonical onto legacy and
 *  skips whenever the Pascal key is already present, and `sanitizeOffers` (`tools/data/sanitizers.ts`), which
 *  writes BOTH halves from whichever it found. No producer writes the Pascal half as a primary value, so the legacy
 *  half is a derived mirror here too, and it goes stale by the same skip guard. */
export const BULK_OFFER_FIELD_KEYS = [CANONICAL_OFFER_COLUMNS.BULK_OFFER, LEGACY_OFFER_FIELDS.BULK_OFFER] as const;

/** Auction-bid read order: canonical column, then the legacy twin. Same direction as the bulk-offer tuple it is read
 *  beside -- the two are the two halves of one bid discriminator at every site that reads either. */
export const AUCTION_BID_FIELD_KEYS = [CANONICAL_OFFER_COLUMNS.AUCTION_BID, LEGACY_OFFER_FIELDS.AUCTION_BID] as const;

/** Buyer-id read order: canonical column, then the legacy twin. `added_user_id` is a THIRD alias at one site
 *  (`tools/offers/construction.ts`) and is deliberately NOT in this tuple: it is a different column
 *  (`types/d1-offers.ts` declares both), so it is a site-specific fallback that belongs after the spread rather than a
 *  spelling of this pair. */
export const BUYER_ID_FIELD_KEYS = [CANONICAL_OFFER_COLUMNS.BUYER_ID, LEGACY_OFFER_FIELDS.BUYER_ID] as const;

/** Days-to-close read order: canonical column, then the legacy twin.
 *
 *  ADDED 2026-09-04, AND ONLY BECAUSE THE WRITER-SIDE GROUNDING BECAME AVAILABLE IN THE SAME CHANGE. The paired rule's
 *  `statusReason` named this pair and `days_to_cancel` as owed work carrying "a LIVE CASING DRIFT ... which one is
 *  authoritative is not decidable from the tree": the allowlist and every reader spelled `Days_to_Close` while
 *  `normalizeOfferToLegacy` wrote `Days_To_Close` (capital `To`), so the pair had NO writer of the spelling anyone read
 *  and the read order could not be grounded the way every tuple above is. It is decidable now, and the census is what
 *  decided it rather than a preference: the capital-`To` spelling occurred at EXACTLY ONE site repo-wide -- the writer
 *  -- against five sites spelling `Days_to_Close`, including the type declaration (`types/api-offer.ts`), the wire
 *  parser (`components/lib/websocket/parse.ts`) and the sanitizer allowlist, which are precisely the three surfaces that
 *  describe the upstream contract. The writer was the outlier and was corrected, not the readers.
 *
 *  `days_to_close` is a real `offers` column (`days_to_close INTEGER`, verified in the migration SQL rather than
 *  assumed) and `flows/offer-create.ts` writes it canonically. Neither `FORM_FIELDS` nor `DB_COLUMNS` declares a member
 *  for it -- checked at both leaves -- so `CANONICAL_OFFER_COLUMNS` above carries the first spelling, exactly as it does
 *  for the three pairs added before it. CANONICAL-FIRST on the same writer-side evidence: after the correction the ONLY
 *  writer of `Days_to_Close` is `normalizeOfferToLegacy`, which copies canonical onto legacy and skips whenever the
 *  Pascal key is already present, so the legacy half is a derived mirror here too and goes stale by the same guard. */
export const DAYS_TO_CLOSE_FIELD_KEYS = [CANONICAL_OFFER_COLUMNS.DAYS_TO_CLOSE, LEGACY_OFFER_FIELDS.DAYS_TO_CLOSE] as const;

/** Days-to-cancel read order: canonical column, then the legacy twin. Same grounding, same census and same single
 *  mirror-writer as {@link DAYS_TO_CLOSE_FIELD_KEYS}; the two are read as a pair at every site that reads either. */
export const DAYS_TO_CANCEL_FIELD_KEYS = [CANONICAL_OFFER_COLUMNS.DAYS_TO_CANCEL, LEGACY_OFFER_FIELDS.DAYS_TO_CANCEL] as const;

/**
 * EVERY governed pair, in one list, so a consumer that needs the VOCABULARY rather than one pair reads it from here.
 *
 * THIS EXPORT EXISTS BECAUSE THE PAIRED RULE'S PROSE WAS FALSE WITHOUT IT. `QUAL-OFFER-LEGACY-FIELD-PAIR-SSOT-001`
 * claimed its governed pairs were "DERIVED AT MODULE LOAD from the two tuples the SSOT exports ... so a third offer
 * pair is governed with zero edits here", while its matcher actually carried a HAND-WRITTEN two-row list naming the two
 * tuples one by one -- so a third tuple was governed only after an import edit and a row edit in the rule. The
 * vocabulary was therefore a decision spelled in TWO places, which is the exact waste that rule exists to convict, and
 * the claim that it was not is what made it invisible. This list is the one spelling; the rule maps over it, and the
 * "zero edits" claim is now true as written. Adding a pair above and to this list governs it everywhere.
 */
export const OFFER_LEGACY_FIELD_KEY_TUPLES = [
	OFFER_AMOUNT_FIELD_KEYS,
	OFFER_STATUS_FIELD_KEYS,
	BULK_OFFER_FIELD_KEYS,
	AUCTION_BID_FIELD_KEYS,
	BUYER_ID_FIELD_KEYS,
	DAYS_TO_CLOSE_FIELD_KEYS,
	DAYS_TO_CANCEL_FIELD_KEYS,
] as const;
